import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, errMsg, fileUrl } from "../../lib/api";

export const Stat = ({ label, value, accent = "text-white", testId }) => (
  <div className="card-rx p-5" data-testid={testId}>
    <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-3 font-mono-rx text-2xl font-semibold ${accent}`}>{value}</p>
  </div>
);

export const Table = ({ head, children, testId, minW = "720px" }) => (
  <div className="card-rx overflow-x-auto">
    <table className="w-full text-sm" style={{ minWidth: minW }} data-testid={testId}>
      <thead><tr className="border-b border-white/[0.06] text-left text-xs uppercase tracking-wider text-slate-500">
        {head.map((h) => <th key={h} className="px-5 py-3.5 font-semibold">{h}</th>)}
      </tr></thead>
      <tbody className="divide-y divide-white/[0.04]">{children}</tbody>
    </table>
  </div>
);

export const Td = ({ children, className = "" }) => <td className={`px-5 py-3.5 ${className}`}>{children}</td>;

export const Modal = ({ open, onClose, title, children, testId }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#111827] sm:max-w-2xl" data-testid={testId}>
      <DialogHeader><DialogTitle className="font-display text-xl text-white">{title}</DialogTitle></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export const Uploader = ({ kind = "image", value, onUploaded, label, testId }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/uploads?kind=${kind}`, fd);
      onUploaded(data);
      toast.success("Uploaded");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-4">
      {kind === "image" && (
        <div className="h-16 w-20 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-[#0b0f17]">
          {value && <img src={fileUrl(value)} alt="" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />}
        </div>
      )}
      <input ref={ref} type="file" hidden accept={kind === "image" ? "image/png,image/jpeg,image/webp,image/gif" : undefined} onChange={pick} data-testid={`${testId}-input`} />
      <button type="button" onClick={() => ref.current.click()} className="btn-ghost py-2" disabled={busy} data-testid={testId}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {label}
      </button>
      {kind === "file" && value && <span className="truncate text-xs text-slate-400">{value}</span>}
    </div>
  );
};

export const Toggle = ({ checked, onChange, testId, label }) => (
  <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-300">
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testId}
      className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-slate-700"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
    {label}
  </label>
);
